import { useContext, useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ControlSettings } from "./controlSettings";
import { GameModes } from "./gameModes/gameModes";
import { themeContext } from "../../context/theme";
import { useAuth } from "../../context/auth";
import { CardOne } from "../../components/cards/cardOne";
import { Guide } from "./guide/guide";
import { Store } from "./store/store";
import { Purchases } from "./purchases/purchases";
import { LasersShooting } from "../../components/svg/lasersShooting";
import { Challenges } from "./singlePlayer/challenges";
import { Challenges as SplitScreenChallenges } from "./splitScreen/challenges";
import { MobileWarning } from "../../components/MobileWarning/MobileWarning";
import { Changelog } from "./changelog/changelog";
import { ProfileModal } from "../../components/modals/ProfileModal";
import styles from "./menu.module.css";
import {
  focusOnMouseMove,
  navigationUserInputHandler,
  playSound,
} from "../../utilities/utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFingerprint,
  faGamepad,
  faKeyboard,
  faMouse,
} from "@fortawesome/free-solid-svg-icons";
import { faGoogle } from "@fortawesome/free-brands-svg-icons";

export const Menu = ({
  setShowMenuOverlay,
  game,
  setShowGameStartTimer,
  setShowGame,
  backgroundSoundTwo,
}) => {
  const [showSettingsOverLay, setShowSettingsOverLay] = useState(false);
  const [showSinglePlayer, setShowSinglePlayer] = useState(false);
  const [showSplitScreen, setShowSplitScreen] = useState(false);
  const [showGameModes, setShowGameModes] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showStore, setShowStore] = useState(false);
  const [showPurchases, setShowPurchases] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const [renderMenu, setRenderMenu] = useState({});
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isUIReady, setIsUIReady] = useState(false);
  const [showOnlineMultiplayerWarning, setShowOnlineMultiplayerWarning] = useState(false);
  const [showScrollUp, setShowScrollUp] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const isMobile = window.innerWidth <= 768;
  const { theme } = useContext(themeContext);
  const { user, signIn, signOut } = useAuth();
  const menuRef = useRef(null);
  const optionsRef = useRef(null);

  const focusableElements = useMemo(() => {
    return { elements: null, index: 0 };
  }, [game.isGameStarted, game.pause]);

  const newGameButtonClickHandler = (event) => {
    game.sounds.clickSound.play();
    game.reset();
    setShowMenuOverlay(false);
    setShowGameStartTimer(true);
  };

  const controlsClickHandler = async () => {
    game.sounds.clickSound.play();
    setShowSettingsOverLay(true);
  };

  const gameModesClickHandler = () => {
    game.sounds.clickSound.play();
    setShowGameModes(true);
  };

  const resumeGameClickHandler = () => {
    game.sounds.clickSound.play();
    if (game.isGameStarted) {
      setShowMenuOverlay(false);
      game.pause = false;
    }
  };

  const guideClickHandler = () => {
    game.sounds.clickSound.play();
    setShowGuide(true);
  };

  const storeClickHandler = () => {
    game.sounds.clickSound.play();
    setShowStore(true);
  };

  const purchasesClickHandler = () => {
    game.sounds.clickSound.play();
    setShowPurchases(true);
  };
  const quitGameClickHandler = () => {
    game.sounds.clickSound.play();
    game.reset();
    setRenderMenu({});
    setShowGame(false);
  };

  const singlePlayerClickHandler = () => {
    game.currentGameMode = "singlePlayerMode";
    game.createPlayers(1);
    game.sounds.clickSound.play();
    setShowSinglePlayer(true);
  };

  const splitScreenClickHandler = () => {
    game.currentGameMode = "splitScreenMode";
    game.sounds.clickSound.play();
    game.createPlayers(2);
    setShowSplitScreen(true);
  };

  // Authentication handlers
  const handleSignIn = async () => {
    try {
      game.sounds.clickSound.play();
      await signIn(window.location.origin + "/");
    } catch (error) {
      // Sign in error handled silently
    }
  };

  const handleSignOut = async () => {
    try {
      game.sounds.clickSound.play();
      setIsSigningOut(true);
      await signOut();
      // Page will reload immediately in signOut, no cleanup needed
    } catch (error) {
      // Sign out error handled silently
      setIsSigningOut(false);
      // Only close modal if signOut failed and page didn't reload
      setShowProfileModal(false);
    }
  };

  // Generate avatar initials from user name or email
  const getAvatarInitials = (user) => {
    if (user.name && user.name.length > 0) {
      return user.name
        .split(" ")
        .map((name) => name[0])
        .join("")
        .substring(0, 2)
        .toUpperCase();
    }
    if (user.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return "U";
  };


  useEffect(() => {
    // Query only focusable elements within the menu container
    if (menuRef.current) {
      focusableElements.elements = menuRef.current.querySelectorAll("[tabindex='0']");
      // Focus on the first menu option (skip auth button/avatar)
      // Auth section is first in DOM, menu options start after it
      const firstMenuOption = Array.from(focusableElements.elements).find(
        el => el.textContent === 'SINGLE PLAYER' || el.textContent === 'RESUME GAME'
      );
      if (firstMenuOption) {
        firstMenuOption.focus();
        focusableElements.index = Array.from(focusableElements.elements).indexOf(firstMenuOption);
      } else {
        focusableElements.elements[0]?.focus();
      }
    }
  }, [game.isGameStarted]);

  useEffect(() => {
    if (
      !showSettingsOverLay &&
      !showGameModes &&
      !showGuide &&
      !showSinglePlayer &&
      !showSplitScreen &&
      !showStore &&
      !showPurchases &&
      !showChangelog &&
      !showProfileModal &&
      !showOnlineMultiplayerWarning
    ) {
      focusableElements.elements[focusableElements.index]?.focus();
    }
  }, [
    showSettingsOverLay,
    showGameModes,
    showGuide,
    showSinglePlayer,
    showSplitScreen,
    showStore,
    showPurchases,
    showChangelog,
    showProfileModal,
    showOnlineMultiplayerWarning,
  ]);

  const gamepadLoop = (currentTime) => {
    // Don't process input until UI animation is complete
    if (!isUIReady) {
      setRenderMenu({});
      return;
    }

    let joystick = null;
    game.joysticks.forEach((element) => {
      if (joystick === null && element !== null) {
        joystick = element;
      }
    });

    if (joystick) {
      // Initialize lastInputTime if not set
      if (!joystick.lastInputTime) {
        joystick.lastInputTime = currentTime;
      }

      const timeSinceLastInput = currentTime - joystick.lastInputTime;
      const throttleDelay = 150; // 150ms for repeat rate (matches useGamepad hook)

      const gamepads = navigator.getGamepads();
      const gamepad = gamepads[joystick.gamepad.index];
      if (gamepad) {
        gamepad.buttons.forEach((button, buttonIndex) => {
          if (button.pressed) {
            if (joystick.previousPressedButtonIndex !== buttonIndex) {
              // New button pressed
              navigationUserInputHandler(
                {
                  key: joystick.navigationKeyBindings[buttonIndex],
                },
                focusableElements,
                game,
                null,
                playSound
              );
              joystick.previousPressedButtonIndex = buttonIndex;
              joystick.lastInputTime = currentTime;
            } else if (timeSinceLastInput >= throttleDelay) {
              // Same button held, throttle by time
              navigationUserInputHandler(
                {
                  key: joystick.navigationKeyBindings[buttonIndex],
                },
                focusableElements,
                game,
                null,
                playSound
              );
              joystick.lastInputTime = currentTime;
            }
          }
        });
      }
    }

    setRenderMenu({});
  };
  useEffect(() => {
    if (
      !showSettingsOverLay &&
      !showGameModes &&
      !showGuide &&
      !showSinglePlayer &&
      !showSplitScreen &&
      !showStore &&
      !showPurchases &&
      !showChangelog &&
      !showProfileModal &&
      !showOnlineMultiplayerWarning
    ) {
      requestAnimationFrame(gamepadLoop);
    }
  }, [
    showSettingsOverLay,
    renderMenu,
    showGameModes,
    showGuide,
    showSinglePlayer,
    showSplitScreen,
    showStore,
    showPurchases,
    showChangelog,
    showProfileModal,
    showOnlineMultiplayerWarning,
  ]);

  useEffect(() => {
    backgroundSoundTwo.loop(true);
    backgroundSoundTwo.volume(0.8);
    return () => {
      backgroundSoundTwo.stop();
    };
  }, []);

  useEffect(() => {
    const isSubScreenOpen =
      showSettingsOverLay ||
      showGameModes ||
      showGuide ||
      showSinglePlayer ||
      showSplitScreen ||
      showStore ||
      showPurchases ||
      showChangelog ||
      showProfileModal ||
      showOnlineMultiplayerWarning;
    if (isSubScreenOpen) {
      backgroundSoundTwo.pause();
    } else {
      backgroundSoundTwo.play();
    }
  }, [
    showSettingsOverLay,
    showGameModes,
    showGuide,
    showSinglePlayer,
    showSplitScreen,
    showStore,
    showPurchases,
    showChangelog,
    showProfileModal,
    showOnlineMultiplayerWarning,
  ]);

  // Wait for UI animation to complete before accepting gamepad input
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsUIReady(true);
    }, 1000); // Match animation-duration: 1s from CSS

    return () => clearTimeout(timer);
  }, []);

  const checkScroll = () => {
    const el = optionsRef.current;
    if (!el) return;
    setShowScrollUp(el.scrollTop > 10);
    setShowScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 10);
  };

  useEffect(() => {
    const el = optionsRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    // Scroll focused option into view within the options container only
    const handleFocus = (e) => {
      const target = e.target;
      if (!target || target === el) return;
      const containerRect = el.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      if (targetRect.bottom > containerRect.bottom) {
        el.scrollTop += targetRect.bottom - containerRect.bottom;
      } else if (targetRect.top < containerRect.top) {
        el.scrollTop -= containerRect.top - targetRect.top;
      }
    };
    el.addEventListener("focusin", handleFocus);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
      el.removeEventListener("focusin", handleFocus);
    };
  }, []);

  useEffect(() => {
    checkScroll();
  }, [game.isGameStarted, game.pause, user]);

  return (
    <>
      <CardOne
        customTag="section"
        attributes={{
          ref: menuRef,
          className: styles["menu-overlay-section"],
          onKeyDown: (event) => {
            if (
              !showSettingsOverLay &&
              !showGameModes &&
              !showGuide &&
              !showSinglePlayer &&
              !showSplitScreen &&
              !showStore &&
              !showPurchases &&
              !showProfileModal &&
              !showOnlineMultiplayerWarning
            ) {
              navigationUserInputHandler(
                event,
                focusableElements,
                game,
                null,
                playSound
              );
            }
          },
          onMouseMove: (event) => {
            if (
              !showSettingsOverLay &&
              !showGameModes &&
              !showGuide &&
              !showSinglePlayer &&
              !showSplitScreen &&
              !showStore &&
              !showPurchases &&
              !showProfileModal &&
              !showOnlineMultiplayerWarning
            ) {
              focusOnMouseMove(event, focusableElements, game, playSound);
            }
          },
          tabIndex: -1,
        }}
      >
        <LasersShooting></LasersShooting>

        {/* Authentication Section - Top Right Corner */}
        <div className={styles["auth-section"]}>
          {user ? (
            <div
              className={
                styles["profile-avatar"] +
                " " +
                styles["profile-avatar-" + theme]
              }
              onClick={() => {
                game.sounds.clickSound.play();
                setShowProfileModal(true);
              }}
              tabIndex={0}
            >
              {getAvatarInitials(user)}
            </div>
          ) : (
            <button
              className={
                styles["auth-button"] + " " + styles["auth-button-" + theme]
              }
              onClick={handleSignIn}
              tabIndex={0}
            >
              <FontAwesomeIcon icon={faGoogle} /> SIGN IN
            </button>
          )}
        </div>
        <div className={styles["logo"] + " " + styles["logo-" + theme]}>
          <b>
            M<span>-</span>tr<span>i</span>s
          </b>
        </div>

        <div className={styles["container"]}>
          <div className={styles["scroll-indicator"] + " " + styles["scroll-indicator-up"]} style={{ visibility: showScrollUp ? "visible" : "hidden" }}>
            ▲
            <span className={styles["scroll-more-label"]}>scroll</span>
          </div>
          {game.isGameStarted && game.pause ? (
            <CardOne
              attributes={{
                className:
                  styles["challenge-details"] +
                  " " +
                  styles["challenge-details-" + theme],
              }}
            >
              {game[game.currentGameMode].challenge.name}
            </CardOne>
          ) : null}

          <div className={styles["options-scroll"]} ref={optionsRef}>
          {!game.isGameStarted ? (
            <>
              <div
                className={styles["option"] + " " + styles["option-" + theme]}
                tabIndex={0}
                onClick={singlePlayerClickHandler}
              >
                SINGLE PLAYER
              </div>
              <div
                className={styles["option"] + " " + styles["option-" + theme]}
                tabIndex={0}
                onClick={splitScreenClickHandler}
              >
                LOCAL MULTIPLAYER
              </div>
              {isMobile ? (
                <div
                  className={styles["option"] + " " + styles["option-" + theme]}
                  tabIndex={0}
                  onClick={() => {
                    game.sounds.clickSound.play();
                    setShowOnlineMultiplayerWarning(true);
                  }}
                >
                  ONLINE MULTIPLAYER
                </div>
              ) : (
                <Link
                  to="/online-multiplayer"
                  className={
                    styles["option"] +
                    " " +
                    styles["option-" + theme] +
                    " " +
                    styles["option-link"]
                  }
                  tabIndex={0}
                  onClick={() => game.sounds.clickSound.play()}
                >
                  ONLINE MULTIPLAYER
                </Link>
              )}
            </>
          ) : null}

          {/* {!game.isGameStarted ? (
                              <div
                                    className={
                                          styles["option"] +
                                          " " +
                                          styles["option-" + theme]
                                    }
                                    tabIndex={0}
                                    onClick={newGameButtonClickHandler}
                              >
                                    NEW GAME
                              </div>
                        ) : null} */}

          {game.isGameStarted && game.pause ? (
            <div
              className={styles["option"] + " " + styles["option-" + theme]}
              tabIndex={0}
              onClick={resumeGameClickHandler}
            >
              RESUME GAME
            </div>
          ) : null}

          <div
            className={styles["option"] + " " + styles["option-" + theme]}
            onClick={controlsClickHandler}
            tabIndex={0}
          >
            CONTROLS
          </div>

          {!game.isGameStarted && !game.pause ? null : (
            <div
              className={styles["option"] + " " + styles["option-" + theme]}
              onClick={quitGameClickHandler}
              tabIndex={0}
            >
              QUIT GAME
            </div>
          )}

          <div
            className={styles["option"] + " " + styles["option-" + theme]}
            onClick={guideClickHandler}
            tabIndex={0}
          >
            GUIDE
          </div>
          <div
            className={styles["option"] + " " + styles["option-" + theme]}
            onClick={storeClickHandler}
            tabIndex={0}
          >
            STORE
          </div>

          {user && !game.isGameStarted && !game.pause ? (
            <div
              className={styles["option"] + " " + styles["option-" + theme]}
              onClick={purchasesClickHandler}
              tabIndex={0}
            >
              PURCHASES
            </div>
          ) : null}
          {!game.isGameStarted && !game.pause ? (
            <div
              className={styles["option"] + " " + styles["option-" + theme]}
              onClick={() => { game.sounds.clickSound.play(); setShowChangelog(true); }}
              tabIndex={0}
            >
              CHANGELOG
            </div>
          ) : null}

          </div>{/* end options-scroll */}
          <div className={styles["scroll-indicator"] + " " + styles["scroll-indicator-down"]} style={{ visibility: showScrollDown ? "visible" : "hidden" }}>
            <span className={styles["scroll-more-label"]}>scroll</span>
            ▼
          </div>
          <div className={styles["controls-icons"]}>
            <FontAwesomeIcon icon={faGamepad}></FontAwesomeIcon>
            <FontAwesomeIcon icon={faMouse}></FontAwesomeIcon>
            <FontAwesomeIcon icon={faKeyboard}></FontAwesomeIcon>
            <FontAwesomeIcon icon={faFingerprint}></FontAwesomeIcon>
          </div>
        </div>
      </CardOne>
      {showSettingsOverLay ? (
        <ControlSettings
          setShowSettingsOverLay={setShowSettingsOverLay}
          game={game}
        ></ControlSettings>
      ) : (
        ""
      )}

      {showGameModes ? (
        <GameModes setShowGameModes={setShowGameModes} game={game}></GameModes>
      ) : null}
      {showGuide ? (
        <Guide setShowGuide={setShowGuide} game={game}></Guide>
      ) : null}

      {showStore ? (
        <Store setShowStore={setShowStore} game={game}></Store>
      ) : null}

      {showPurchases ? (
        <Purchases setShowPurchases={setShowPurchases} game={game}></Purchases>
      ) : null}

      {showChangelog ? (
        <Changelog setShowChangelog={setShowChangelog} game={game} />
      ) : null}

      {showSinglePlayer ? (
        <Challenges
          game={game}
          setShowMenuOverlay={setShowMenuOverlay}
          setShowGameStartTimer={setShowGameStartTimer}
          setShowSinglePlayer={setShowSinglePlayer}
        ></Challenges>
      ) : null}
      {showSplitScreen ? (
        isMobile ? (
          <MobileWarning game={game} onBack={() => setShowSplitScreen(false)} />
        ) : (
          <SplitScreenChallenges
            game={game}
            setShowMenuOverlay={setShowMenuOverlay}
            setShowGameStartTimer={setShowGameStartTimer}
            setShowSplitScreen={setShowSplitScreen}
          ></SplitScreenChallenges>
        )
      ) : null}

      {showOnlineMultiplayerWarning ? (
        <MobileWarning game={game} onBack={() => setShowOnlineMultiplayerWarning(false)} />
      ) : null}

      {user && showProfileModal ? (
        <ProfileModal
          isVisible={showProfileModal}
          user={user}
          onSignOut={handleSignOut}
          onClose={() => setShowProfileModal(false)}
          game={game}
          isSigningOut={isSigningOut}
        />
      ) : null}
    </>
  );
};
